import React, { useState, useRef, useEffect } from "react";
import { profile, skillsList, projects } from "../../data/content";

interface CommandHistoryItem {
  id: string;
  command: string;
  output: React.ReactNode;
}

interface GhosttyShellProps {
  onSwitchTab?: (tab: "flappy" | "shell" | "matrix") => void;
}

export const GhosttyShell: React.FC<GhosttyShellProps> = ({ onSwitchTab }) => {
  const [inputVal, setInputVal] = useState("");
  const [history, setHistory] = useState<CommandHistoryItem[]>([
    {
      id: "welcome",
      command: "welcome",
      output: (
        <div className="space-y-1 text-[#A6A39A]">
          <p className="text-[#E5983A] font-bold">Ghostty v1.1.2 (x86_64-linux-edge)</p>
          <p>Type <span className="text-[#F3F2EE] font-semibold">help</span> to view available commands, or run <span className="text-[#F3F2EE] font-semibold">neofetch</span>.</p>
        </div>
      ),
    },
  ]);
  const [cmdHistory, setCmdHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);

  const terminalContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Scroll ONLY the internal terminal box, never touch the browser window scroll
    if (terminalContainerRef.current) {
      terminalContainerRef.current.scrollTop = terminalContainerRef.current.scrollHeight;
    }
  }, [history]);

  const handleCommand = async (cmd: string) => {
    const trimmed = cmd.trim();
    if (!trimmed) return;

    setCmdHistory((prev) => [...prev, trimmed]);
    setHistoryIdx(-1);

    const parts = trimmed.split(" ");
    const mainCmd = parts[0].toLowerCase();
    const arg = parts.slice(1).join(" ").toLowerCase();

    let outputNode: React.ReactNode = null;

    switch (mainCmd) {
      case "help":
        outputNode = (
          <div className="space-y-1 text-[#C8C6BE]">
            <p className="text-[#E5983A] font-semibold">Available Commands:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div><span className="text-[#F3F2EE] font-mono">neofetch</span> - Display developer specs & system info</div>
              <div><span className="text-[#F3F2EE] font-mono">play</span> - Launch ASCII Flappy Bird game</div>
              <div><span className="text-[#F3F2EE] font-mono">curl [endpoint]</span> - Query live /v1/engineer API</div>
              <div><span className="text-[#F3F2EE] font-mono">skills</span> - List backend & systems skills</div>
              <div><span className="text-[#F3F2EE] font-mono">projects</span> - View selected repositories</div>
              <div><span className="text-[#F3F2EE] font-mono">matrix</span> - Launch ASCII digital rain</div>
              <div><span className="text-[#F3F2EE] font-mono">whoami</span> - Display current user identity</div>
              <div><span className="text-[#F3F2EE] font-mono">contact</span> - Show direct contact coordinates</div>
              <div><span className="text-[#F3F2EE] font-mono">clear</span> - Clear terminal scrollback</div>
            </div>
          </div>
        );
        break;

      case "neofetch":
        outputNode = (
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 py-1 text-xs font-mono">
            <pre className="text-[#E5983A] leading-tight select-none">
{`      .-.      
     (o.o)     
      |=|      
     __|__     
   /       \\   
  (  Node   )  
   \\__   __/   
     /   \\     
    /     \\    `}
            </pre>
            <div className="space-y-1 text-[#C8C6BE]">
              <p className="text-[#F3F2EE] font-bold">{profile.githubHandle}@tehran</p>
              <p className="text-[#4E4C46]">----------------------</p>
              <p><span className="text-[#E5983A]">OS:</span> Linux 6.8 (Cloudflare Pages Edge)</p>
              <p><span className="text-[#E5983A]">Host:</span> {profile.website.replace("https://", "")} / Tehran Edge Node</p>
              <p><span className="text-[#E5983A]">Role:</span> {profile.role}</p>
              <p><span className="text-[#E5983A]">Uptime:</span> 2019 — Present (5+ years in production)</p>
              <p><span className="text-[#E5983A]">Shell:</span> ghostty 1.1.2 (zsh)</p>
              <p><span className="text-[#E5983A]">Primary Stack:</span> TypeScript, Node.js, Python, Docker</p>
              <p><span className="text-[#E5983A]">Availability:</span> {profile.availability}</p>
              <p><span className="text-[#E5983A]">Location:</span> {profile.location} ({profile.timezoneDisplay})</p>
            </div>
          </div>
        );
        break;

      case "curl":
        try {
          const target = arg || "/v1/engineer";
          const res = await fetch(target.startsWith("http") ? target : "/v1/engineer");
          const json = await res.json();
          outputNode = (
            <div className="space-y-1 text-xs">
              <p className="text-[#4ADE80]">HTTP/2 200 OK · content-type: application/json</p>
              <pre className="text-[#DCD8CE] bg-[#10100F] p-2.5 rounded-lg border border-[#242320] overflow-x-auto">
                {JSON.stringify(json, null, 2)}
              </pre>
            </div>
          );
        } catch (e) {
          outputNode = <p className="text-[#EF4444]">curl: (6) Could not resolve host or query endpoint.</p>;
        }
        break;

      case "skills":
        outputNode = (
          <div className="space-y-1 text-xs">
            <p className="text-[#E5983A] font-bold">Backend & Infrastructure Skills:</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {skillsList.map((skill) => (
                <span key={skill} className="px-2 py-0.5 rounded bg-[#201F1D] border border-[#2E2C28] text-[#DCD8CE]">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        );
        break;

      case "projects":
        outputNode = (
          <div className="space-y-2 text-xs">
            <p className="text-[#E5983A] font-bold">Selected Projects:</p>
            {projects.map((p) => (
              <div key={p.name} className="border-l-2 border-[#E5983A] pl-2.5 py-0.5">
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[#F3F2EE] hover:underline font-bold">
                  {p.name} ↗
                </a>
                <p className="text-[#8C8A84]">{p.description}</p>
              </div>
            ))}
          </div>
        );
        break;

      case "play":
      case "flappy":
        outputNode = <p className="text-[#4ADE80]">Switching to flappy.sh...</p>;
        setTimeout(() => onSwitchTab?.("flappy"), 300);
        break;

      case "matrix":
        outputNode = <p className="text-[#4ADE80]">Launching matrix.sh digital rain...</p>;
        setTimeout(() => onSwitchTab?.("matrix"), 300);
        break;

      case "whoami":
        outputNode = <p className="text-[#F3F2EE]">{profile.name} — {profile.role}</p>;
        break;

      case "contact":
        outputNode = (
          <div className="space-y-1 text-xs">
            <p><span className="text-[#E5983A]">Email:</span> {profile.email}</p>
            <p><span className="text-[#E5983A]">GitHub:</span> {profile.github}</p>
            <p><span className="text-[#E5983A]">LinkedIn:</span> {profile.linkedin}</p>
            <p><span className="text-[#E5983A]">Phone:</span> {profile.phoneDisplay}</p>
          </div>
        );
        break;

      case "uptime":
        outputNode = <p className="text-[#A6A39A]">5 years, 48 days, 14 hours. Load average: 0.08, 0.04, 0.01</p>;
        break;

      case "clear":
        setHistory([]);
        return;

      default:
        outputNode = (
          <p className="text-[#EF4444]">
            zsh: command not found: {trimmed}. Type <span className="text-[#F3F2EE] underline cursor-pointer" onClick={() => handleCommand("help")}>help</span> for a list of commands.
          </p>
        );
    }

    setHistory((prev) => [
      ...prev,
      {
        id: Math.random().toString(),
        command: trimmed,
        output: outputNode,
      },
    ]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleCommand(inputVal);
      setInputVal("");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cmdHistory.length > 0) {
        const nextIdx = historyIdx + 1 < cmdHistory.length ? historyIdx + 1 : historyIdx;
        setHistoryIdx(nextIdx);
        setInputVal(cmdHistory[cmdHistory.length - 1 - nextIdx] || "");
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIdx > 0) {
        const nextIdx = historyIdx - 1;
        setHistoryIdx(nextIdx);
        setInputVal(cmdHistory[cmdHistory.length - 1 - nextIdx] || "");
      } else if (historyIdx === 0) {
        setHistoryIdx(-1);
        setInputVal("");
      }
    }
  };

  return (
    <div
      ref={terminalContainerRef}
      className="p-4 sm:p-5 font-mono text-xs sm:text-[13px] leading-relaxed bg-[#121110] min-h-[300px] max-h-[420px] overflow-y-auto flex flex-col justify-between select-text"
      onClick={() => inputRef.current?.focus({ preventScroll: true })}
    >
      <div className="space-y-3.5">
        {history.map((item) => (
          <div key={item.id} className="space-y-1.5">
            {item.command !== "welcome" && (
              <div className="flex items-center gap-2 text-[#8C8A84]">
                <span className="text-[#E5983A] font-semibold">roustaie@tehran:~$</span>
                <span className="text-[#F3F2EE]">{item.command}</span>
              </div>
            )}
            <div>{item.output}</div>
          </div>
        ))}
      </div>

      {/* Live Input Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCommand(inputVal);
          setInputVal("");
        }}
        className="flex items-center gap-2 pt-3 border-t border-[#242320] mt-4"
      >
        <span className="text-[#E5983A] font-semibold shrink-0">roustaie@tehran:~$</span>
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck="false"
          placeholder="type 'help', 'neofetch', or 'play'..."
          className="w-full bg-transparent text-[#F3F2EE] focus:outline-none placeholder:text-[#55534D] font-mono text-xs sm:text-[13px]"
        />
      </form>
    </div>
  );
};
